import type { DeliveryImpactResponse } from '../../../api/agent-miner-client/agent-miner-client.type';
import { inkDark, primaryDarkest, redDarkest } from '../../../styles/design-tokens';
import { getTimeAllocationChartLabels } from '../../process-analysis/components/line-chart/line-chart.helpers';
import type { ChartDataItem } from '../../process-analysis/components/line-chart/line-chart.type';
import { STANDARD_DATE_FORMATS, formatDate } from '../../../helpers/date-time-helpers';
import { EM_DASH, deriveAllocationType } from '../model-comparison/model-comparison.helpers';
import type { MetricCardConfig, MetricDirection } from './delivery-impact.type';

/**
 * The six comparison cards, in display order (3×2 grid). Directions drive the
 * delta chip's color.
 */
const METRIC_CARDS: MetricCardConfig[] = [
  {
    key: 'median_cycle_time_days',
    labelKey: 'agent_miner.delivery_impact.metrics.median_cycle_time',
    infoKey: 'agent_miner.delivery_impact.metric_info.median_cycle_time',
    unit: 'd',
    direction: 'lower_better',
  },
  {
    key: 'median_lead_time_days',
    labelKey: 'agent_miner.delivery_impact.metrics.median_lead_time',
    infoKey: 'agent_miner.delivery_impact.metric_info.median_lead_time',
    unit: 'd',
    direction: 'lower_better',
  },
  {
    key: 'throughput_per_dev_per_week',
    labelKey: 'agent_miner.delivery_impact.metrics.throughput',
    infoKey: 'agent_miner.delivery_impact.metric_info.throughput',
    unit: '',
    direction: 'higher_better',
  },
  {
    key: 'rework_rate_pct',
    labelKey: 'agent_miner.delivery_impact.metrics.rework_rate',
    infoKey: 'agent_miner.delivery_impact.metric_info.rework_rate',
    unit: '%',
    direction: 'lower_better',
  },
  {
    key: 'median_review_to_done_days',
    labelKey: 'agent_miner.delivery_impact.metrics.review_to_done',
    infoKey: 'agent_miner.delivery_impact.metric_info.review_to_done',
    unit: 'd',
    direction: 'lower_better',
  },
  {
    key: 'median_time_to_merge_days',
    labelKey: 'agent_miner.delivery_impact.metrics.time_to_merge',
    infoKey: 'agent_miner.delivery_impact.metric_info.time_to_merge',
    unit: 'd',
    direction: 'lower_better',
  },
];

/**
 * Formats a backend delta percentage as a signed chip label with a direction
 * arrow (e.g. "▼ 35%", "▲ 12%"), or an em-dash when the delta is missing.
 *
 * @param {number | null | undefined} deltaPct - Backend-computed delta percentage
 * @returns {string} The chip label
 */
const formatDelta = (deltaPct: number | null | undefined): string => {
  if (deltaPct === null || deltaPct === undefined) return EM_DASH;
  if (deltaPct === 0) return '0%';
  const arrow = deltaPct > 0 ? '▲' : '▼';
  return `${arrow} ${Math.abs(deltaPct)}%`;
};

/**
 * Resolves the delta chip's color from the metric's improvement direction:
 * green when the AI side moved the right way, red when it moved the wrong
 * way, ink for neutral metrics, zero deltas, and missing data.
 *
 * @param {number | null | undefined} deltaPct - Backend-computed delta percentage
 * @param {MetricDirection} direction - Which way reads as an improvement
 * @returns {string} A design-token color
 */
const deltaChipColor = (deltaPct: number | null | undefined, direction: MetricDirection): string => {
  if (deltaPct === null || deltaPct === undefined || deltaPct === 0 || direction === 'neutral') return inkDark;
  const isImprovement = direction === 'lower_better' ? deltaPct < 0 : deltaPct > 0;
  return isImprovement ? primaryDarkest : redDarkest;
};

/** Compact `MMM D` axis/tooltip formatter shared by every period label. */
const formatPeriodLabel = (date: string): string => formatDate(date, { format: STANDARD_DATE_FORMATS.MONTH_DAY });

/** Chart series keys for the trend chart's two cohorts. */
const AI_SERIES_KEY = 'ai';
const BASELINE_SERIES_KEY = 'baseline';

/**
 * Maps the two-cohort cycle-time series into the shared chart data shape: one
 * row per period with `ai`/`baseline` median values. Nulls are preserved so
 * the chart shows gaps for periods without completions. Tick/tooltip labels
 * are delegated to process-analysis's `getTimeAllocationChartLabels`, matching
 * the Model Comparison chart.
 *
 * @param {DeliveryImpactResponse} response - The delivery impact payload
 * @returns {ChartDataItem[]} One chart row per period
 */
const buildDeliveryTrendData = (response: DeliveryImpactResponse): ChartDataItem[] => {
  const timeAllocationType =
    response.meta.time_allocation_type ?? deriveAllocationType(response.meta.start_date, response.meta.end_date);

  return response.meta.periods.map((period) => {
    const { tickLabel, tooltipLabel } = getTimeAllocationChartLabels({
      rawDate: period.id,
      timeAllocationType,
      timeAllocations: response.meta.periods,
      formatDateLabel: formatPeriodLabel,
    });
    return {
      date: tickLabel,
      rawDate: period.id,
      dateRange: tooltipLabel,
      [AI_SERIES_KEY]: response.cycle_time_series.ai.median_days[period.id] ?? null,
      [BASELINE_SERIES_KEY]: response.cycle_time_series.baseline.median_days[period.id] ?? null,
    };
  });
};

/**
 * True when the window has no completed tasks in either cohort — the tab-level
 * empty state.
 *
 * @param {DeliveryImpactResponse} response - The delivery impact payload
 * @returns {boolean} Whether the population is empty
 */
const isDeliveryImpactEmpty = (response: DeliveryImpactResponse): boolean =>
  response.summary.ai_tasks_completed === 0 && response.summary.baseline_tasks_completed === 0;

export {
  AI_SERIES_KEY,
  BASELINE_SERIES_KEY,
  METRIC_CARDS,
  buildDeliveryTrendData,
  deltaChipColor,
  formatDelta,
  isDeliveryImpactEmpty,
};
